import { MutableRefObject } from 'react';

export const smoothScrollToTop = (): Promise<void> =>
  new Promise((resolve) => {
    const scrollFunction = () => {
      const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;

      if (currentScroll === 0) {
        return resolve(void 0);
      }

      if (currentScroll > 0) {
        window.requestAnimationFrame(scrollFunction);
        window.scrollTo(0, currentScroll - currentScroll / 3);
      }
    };
    scrollFunction();
  });

export enum ScrollSpeed {
  FAST = 'FAST',
  MEDIUM = 'MEDIUM',
  SLOW = 'SLOW'
}

const speeds = {
  [ScrollSpeed.FAST]: 100,
  [ScrollSpeed.MEDIUM]: 50,
  [ScrollSpeed.SLOW]: 20
};

export const smoothScrollToFirstInvalidField = async ({
  speed,
  offset = 0
}: {
  speed: ScrollSpeed;
  offset: number;
}): Promise<void> => {
  const firstInvalidField = document.querySelectorAll<HTMLElement>('[aria-invalid="true"]')[0];

  //  return if can't find a focusable invalid field
  if (
    !firstInvalidField ||
    typeof firstInvalidField.focus !== 'function' ||
    typeof firstInvalidField.getBoundingClientRect !== 'function'
  ) {
    return;
  }

  const scrollPosition = firstInvalidField.getBoundingClientRect().top;
  const diff = Math.abs(Math.abs(scrollPosition) - Math.abs(offset));
  if (diff < 2 || scrollPosition >= offset) {
    firstInvalidField.focus();
    return;
  }

  const scrolledToInvalidField = await new Promise((resolve) => {
    const scrollFunction = () => {
      const currentScroll = firstInvalidField.getBoundingClientRect().top;

      if (currentScroll === 0) {
        resolve(true);
      } else if (currentScroll < 0) {
        const nextScrollStep = currentScroll + speeds[speed];

        if (nextScrollStep > 0) {
          const revisedScrollStep = speeds[speed] - nextScrollStep;
          window.scrollBy(0, -revisedScrollStep);
          return resolve(true);
        }

        window.requestAnimationFrame(scrollFunction);
        window.scrollBy(0, -speeds[speed]);
      } else {
        return resolve(false);
      }
    };
    scrollFunction();
  });

  if (scrolledToInvalidField) {
    offset && window.scrollBy(0, offset);
    firstInvalidField.focus();
  } else {
    //  Unable to scroll to invalid field.  Focus on invalid field
    firstInvalidField.focus();
  }
};

export const smoothScrollTo = async (
  ref: MutableRefObject<any>,
  callBack: (setScrolling: boolean) => void,
  speed: ScrollSpeed,
  offset = 0
): Promise<void> => {
  const scrollPosition = ref.current.getBoundingClientRect().top;
  const diff = Math.abs(Math.abs(scrollPosition) - Math.abs(offset));

  if (diff < 2 || scrollPosition >= offset) {
    return;
  }
  callBack(true);
  const scrolledToRef = await new Promise((resolve) => {
    const scrollFunction = () => {
      const currentScroll = ref.current.getBoundingClientRect().top;
      if (currentScroll === 0) {
        resolve(true);
      } else if (currentScroll < 0) {
        const nextScrollStep = currentScroll + speeds[speed];

        if (nextScrollStep > 0) {
          const revisedScrollStep = speeds[speed] - nextScrollStep;
          window.scrollBy(0, -revisedScrollStep);
          return resolve(true);
        }

        window.requestAnimationFrame(scrollFunction);
        window.scrollBy(0, -speeds[speed]);
      } else {
        return resolve(false);
      }
    };
    scrollFunction();
  });

  if (scrolledToRef) {
    offset && window.scrollBy(0, offset);
  } else {
    //  Unable to scroll to ref.  Go to top of page
    smoothScrollToTop();
  }
  callBack(false);
};

const getSpeedWithEasing = (currentOffset: number, speed: number) => {
  const isNegative = currentOffset < 0,
    offset = isNegative ? currentOffset * -1 : currentOffset;

  let speedWithEasing = speed;

  if (offset >= 300 && offset < 500) {
    speedWithEasing = speed * 0.7;
  }
  if (offset >= 100 && offset < 300) {
    speedWithEasing = speed * 0.45;
  }
  if (offset >= 50 && offset < 100) {
    speedWithEasing = speed * 0.3;
  }
  if (offset >= 20 && offset < 50) {
    speedWithEasing = speed * 0.15;
  }
  if (offset > 0 && offset < 20) {
    speedWithEasing = speed * 0.1;
  }

  return isNegative ? speedWithEasing / -1 : speedWithEasing;
};

export const animateScrollUp = async ({
  ref,
  speed,
  offset = 0,
  callback
}: {
  ref: MutableRefObject<any>;
  speed: number;
  offset?: number;
  callback?: (setScrolling: boolean) => void;
}): Promise<void> => {
  if (callback) {
    callback(true);
  }

  const scrolledToRef = await new Promise((resolve) => {
    const scrollFunction = () => {
      const currentScroll = ref.current.getBoundingClientRect().top,
        speedWithEasing = getSpeedWithEasing(currentScroll, speed);

      if (currentScroll === 0) {
        resolve(true);
      } else if (currentScroll < 0) {
        const nextScrollStep = currentScroll + speed;

        if (nextScrollStep >= offset) {
          window.scrollBy(0, -offset);
          return resolve(true);
        }

        window.requestAnimationFrame(scrollFunction);
        window.scrollBy(0, speedWithEasing);
      }
    };
    scrollFunction();
  });

  if (scrolledToRef) {
    offset && window.scrollBy(0, offset);
  } else {
    //  Unable to scroll to ref.  Go to top of page
    smoothScrollToTop();
  }

  if (callback) {
    callback(false);
  }
};

export const animateScrollDown = async ({
  ref,
  speed,
  offset = 0,
  callback
}: {
  ref: MutableRefObject<any>;
  speed: number;
  offset?: number;
  callback?: (setScrolling: boolean) => void;
}): Promise<void> => {
  if (callback) {
    callback(true);
  }

  await new Promise((resolve) => {
    let previousScroll = 0;

    const scrollFunction = () => {
      const currentScroll = ref.current.getBoundingClientRect().top,
        speedWithEasing = getSpeedWithEasing(currentScroll, speed);

      if (currentScroll === 0) {
        resolve(true);
      } else if (currentScroll > 0) {
        if (currentScroll === previousScroll) {
          // case 1: scroll cannot proceed further, meaning we've reached the end of the page
          // case 2: scroll is stuck in the same position for some other reason
          return resolve(true);
        } else {
          previousScroll = currentScroll;
          const nextScrollStep = currentScroll - speed;

          if (nextScrollStep <= offset) {
            window.scrollBy(0, offset);
            return resolve(true);
          }

          window.requestAnimationFrame(scrollFunction);
          window.scrollBy(0, +speedWithEasing);
        }
      } else {
        return resolve(false);
      }
    };
    scrollFunction();
  });
};

export const animateScrollTo = async ({
  ref,
  speed,
  offset = 0,
  callback
}: {
  ref: MutableRefObject<any>;
  speed: ScrollSpeed;
  offset?: number;
  callback?: (setScrolling: boolean) => void;
}): Promise<void> => {
  const speeds = {
    [ScrollSpeed.FAST]: 100,
    [ScrollSpeed.MEDIUM]: 50,
    [ScrollSpeed.SLOW]: 20
  };

  const scrollPosition = ref.current.offsetTop,
    scrollSpeed = speeds[speed],
    scrollMethod = window.scrollY > scrollPosition ? animateScrollUp : animateScrollDown;

  scrollMethod({ ref, speed: scrollSpeed, offset, callback });
};

export const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 0);
};
